import React, { useState, useEffect, useCallback } from 'react';
import Header from './components/layout/Header';
import Section from './components/layout/Section';
import Hero from './components/sections/Hero';
import About from './components/sections/About';
import Experience from './components/sections/Experience';
import DexterityFactions from './components/sections/DexterityFactions';
import Projects from './components/sections/Projects';
import Skills from './components/sections/Skills';
import Contact from './components/sections/Contact';
import CursorEffect from './components/CursorEffect';

const SECTIONS = ['home', 'about', 'experience', 'dexterity', 'projects', 'skills', 'contact'];

function App() {
  const [currentSection, setCurrentSection] = useState(0);

  // Track which section is currently in view for header styling
  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    SECTIONS.forEach((sectionId, index) => {
      const element = document.getElementById(sectionId);
      if (!element) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
            setCurrentSection(index);
          }
        },
        { threshold: [0.3, 0.5, 0.7] }
      );

      observer.observe(element);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  // Navigate to section
  const navigateToSection = useCallback((index: number) => {
    const sectionId = SECTIONS[index];
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Check if in dark section (Contact)
  const isInDarkSection = currentSection === SECTIONS.length - 1;

  return (
    <div className="relative w-full min-h-screen bg-slate-50 font-sans text-slate-900">
      {/* Custom cursor (desktop only) */}
      <CursorEffect />

      {/* Header */}
      <Header isInDarkSection={isInDarkSection} />

      {/* Main Content */}
      <main>
        {/* Hero Section */}
        <Section id="home" className="flex items-center justify-center">
          <Hero />
        </Section>

        {/* About Section */}
        <Section id="about" className="flex items-center justify-center py-16 md:py-0">
          <About />
        </Section>

        {/* Experience Section */}
        <Section id="experience" className="flex items-center justify-center py-16 md:py-0">
          <Experience />
        </Section>

        {/* Dexterity Factions Section - Video Background */}
        <Section id="dexterity" className="relative" animateOut={false}>
          <DexterityFactions />
        </Section>

        {/* Projects Section */}
        <Section id="projects" className="flex items-center justify-center py-16 md:py-0">
          <Projects />
        </Section>

        {/* Skills Section */}
        <Section id="skills" className="flex items-center justify-center py-16 md:py-0">
          <Skills />
        </Section>

        {/* Contact Section - Dark Background */}
        <Section id="contact" dark className="flex items-center justify-center">
          <Contact />
        </Section>
      </main>

      {/* Progress Indicator */}
      <div className="fixed right-3 md:right-6 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-2 md:gap-4">
        {SECTIONS.map((section, i) => (
          <button
            key={section}
            onClick={() => navigateToSection(i)}
            className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full transition-all duration-300 hover:scale-150 ${
              currentSection === i
                ? 'bg-matcha-600 scale-150'
                : isInDarkSection
                ? 'bg-slate-500 hover:bg-matcha-400'
                : 'bg-slate-300 hover:bg-matcha-400'
            }`}
            aria-label={`Navigate to ${section}`}
          />
        ))}
      </div>
    </div>
  );
}

export default App;
